import React, { useEffect, useState } from "react"
import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  CardTitle,
  Label,
  Button,
} from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { editCustomerSchema } from "components/Common/validation"
import {
  getCustomerDetails,
  updateCustomer,
} from "store/actions/customersAction"
import { useSelector } from "react-redux"
import Spinners from "components/Shared/Spinners"
import { showAlert } from "store/reducers/alertReducer"
import BackButton from "components/Shared/BackButton"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const initialValues = {
  firstName: "",
  lastName: "",
  phoneCode: "",
  phoneNumber: "",
  email: "",
}

const EditCustomer = () => {
  const { t } = useTranslation()

  document.title = t(`Edit Customer | Namq`)

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const { loading, isLoading, customerDetails } = useSelector(
    state => state.Customers
  )

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      7,
      "customersPermission_2",
      accountType
    )

    if (!isPermissionEdit) {
      navigate("/dashboard")
    }
  }, [])

  useEffect(() => {
    fetchCustomerDetails()
  }, [])

  const fetchCustomerDetails = () => {
    const payload = {
      id,
    }
    dispatch(getCustomerDetails(payload))
  }

  const [formValues, setFormValues] = useState(initialValues)

  useEffect(() => {
    if (customerDetails) {
      const { firstName, lastName, phoneCode, phoneNumber, email } =
        customerDetails
      setFormValues({
        firstName,
        lastName,
        phoneCode,
        phoneNumber,
        email,
      })
    }
  }, [customerDetails])

  if (loading) return <Spinners />

  const handleOnSubmit = values => {
    const { firstName, lastName, phoneCode, phoneNumber, email } = values
    const payload = {
      id,
      firstName,
      lastName,
      email,
    }
    dispatch(updateCustomer(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          navigate(-1)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs />
        <Row>
          <Col>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton /> {t("Edit Customer")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={editCustomerSchema}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, errors, touched }) => (
                    <Form>
                      <Row>
                        <Col md={4}>
                          <div className="form_field">
                            <Label
                              htmlFor="firstName"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>{" "}
                              {t("First Name")}
                            </Label>
                            <Field
                              name="firstName"
                              type="text"
                              id="firstName"
                              placeholder={t("First Name")}
                              className={`form-control ${
                                touched.firstName && errors.firstName
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="firstName" />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="form_field">
                            <Label
                              htmlFor="lastName"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>{" "}
                              {t("Last Name")}
                            </Label>
                            <Field
                              name="lastName"
                              type="text"
                              id="lastName"
                              placeholder={t("Last Name")}
                              className={`form-control ${
                                touched.lastName && errors.lastName
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="lastName" />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="form_field">
                            <Label
                              htmlFor="phoneCode"
                              className="col col-form-label"
                            >
                              {t("Phone Code")}
                            </Label>
                            <Field
                              name="phoneCode"
                              type="text"
                              id="phoneCode"
                              readOnly
                              placeholder={t("Phone Code")}
                              className={`not_allowed_cursor form-control ${
                                touched.phoneCode && errors.phoneCode
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="form_field">
                            <Label
                              htmlFor="phoneNumber"
                              className="col col-form-label"
                            >
                              {t("Phone Number")}
                            </Label>
                            <Field
                              name="phoneNumber"
                              type="text"
                              id="phoneNumber"
                              readOnly
                              placeholder={t("Phone Number")}
                              className={`not_allowed_cursor form-control ${
                                touched.phoneNumber && errors.phoneNumber
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                          </div>
                        </Col>
                        <Col sm={4}>
                          <div className="form_field">
                            <Label
                              htmlFor="email"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>{" "}
                              {t("Email")}
                            </Label>
                            <Field
                              type="email"
                              name="email"
                              className={`form-control ${
                                touched.email && errors.email
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="horizontal-email-Input"
                              placeholder={t("Email")}
                            />
                            <TextError name="email" />
                          </div>
                        </Col>
                      </Row>
                      <Row className="justify-content-end">
                        <Col sm={12}>
                          <div className="d-flex justify-content-end">
                            <Button
                              type="button"
                              color="secondary"
                              onClick={() => navigate(-1)}
                            >
                              {t("Cancel")}
                            </Button>
                            <Button
                              className="mx-2"
                              type="submit"
                              color="success"
                              disabled={isLoading}
                            >
                              {t("Save")}
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default EditCustomer
